import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Table, Tag, message } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import tmAdminService from '../../services/tmService';
import { fmtTime, ok, toastErr } from './common';

const REASONS = [
    { value: 'hard_bounce', label: '硬退信' }, { value: 'complaint', label: '投诉' },
    { value: 'unsubscribe', label: '退订' }, { value: 'manual', label: '手动' },
];
const SCOPES = [{ value: 'global', label: '全局' }, { value: 'user', label: '用户' }, { value: 'stream', label: '发信流' }];
const SCOPE_COLOR = { global: 'red', user: 'blue', stream: 'default' };

const Suppressions = () => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState({ current: 1, pageSize: 50 });
    const [f, setF] = useState({ scope: 'global' });
    const [adding, setAdding] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();
    const scope = Form.useWatch('scope', form);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await tmAdminService.listSuppressions({ ...f, page: page.current, per_page: page.pageSize });
            setRows(r?.data?.items || []);
            setTotal(r?.data?.total || 0);
        } catch (e) {
            toastErr(e, '加载失败');
        } finally {
            setLoading(false);
        }
    }, [f, page]);

    useEffect(() => { load(); }, [load]);

    const setFilter = (patch) => { setF((x) => ({ ...x, ...patch })); setPage((p) => ({ ...p, current: 1 })); };

    const add = async () => {
        const v = await form.validateFields();
        const emails = v.emails.split(/[\s,;]+/).filter(Boolean);
        setSaving(true);
        try {
            const r = await tmAdminService.createSuppression({
                emails, scope: v.scope, scope_id: v.scope === 'global' ? 0 : v.scope_id, reason: v.reason,
                category: v.category || undefined, note: v.note || undefined,
            });
            if (ok(r)) {
                const inv = r.data?.invalid || [];
                (inv.length ? message.warning : message.success)(`已添加 ${r.data?.added || 0}${inv.length ? `，无效 ${inv.length}：${inv.slice(0, 5).join(', ')}` : ''}`);
                setAdding(false);
                form.resetFields();
                load();
            }
        } catch (e) {
            toastErr(e, '添加失败');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (r) => {
        try {
            const res = await tmAdminService.deleteSuppression(r.id);
            if (ok(res)) { message.success('已移除'); load(); }
        } catch (e) {
            toastErr(e, '移除失败');
        }
    };

    const columns = [
        { title: 'ID', dataIndex: 'id', width: 70 },
        { title: '邮箱', dataIndex: 'email' },
        { title: '范围', key: 'scope', width: 130, render: (_, r) => <span><Tag color={SCOPE_COLOR[r.scope]}>{SCOPES.find((s) => s.value === r.scope)?.label || r.scope}</Tag>{r.scope !== 'global' ? `#${r.scope_id}` : ''}</span> },
        { title: '原因', dataIndex: 'reason', width: 90, render: (v) => REASONS.find((x) => x.value === v)?.label || v },
        { title: '类别', dataIndex: 'category', width: 110, render: (v) => v || '全部' },
        { title: '备注', dataIndex: 'note', ellipsis: true },
        { title: '时间', dataIndex: 'created_at', width: 160, render: fmtTime },
        {
            title: '', key: 'op', width: 70,
            render: (_, r) => <Popconfirm title="移除后可再次发信" onConfirm={() => remove(r)}><Button type="link" size="small" danger>移除</Button></Popconfirm>,
        },
    ];

    return (
        <Card title="抑制名单" extra={<Space><Button icon={<ReloadOutlined />} onClick={load} /><Button type="primary" icon={<PlusOutlined />} onClick={() => setAdding(true)}>添加</Button></Space>}>
            <Space wrap style={{ marginBottom: 12 }}>
                <Select allowClear placeholder="范围" style={{ width: 110 }} value={f.scope} options={SCOPES} onChange={(v) => setFilter({ scope: v })} />
                {f.scope && f.scope !== 'global' && <InputNumber placeholder="用户/流 ID" min={1} style={{ width: 130 }} onChange={(v) => setFilter({ scope_id: v || undefined })} />}
                <Select allowClear placeholder="原因" style={{ width: 110 }} options={REASONS} onChange={(v) => setFilter({ reason: v })} />
                <Input.Search allowClear placeholder="完整邮箱" style={{ width: 240 }} onSearch={(v) => setFilter({ email: v.trim() || undefined })} />
            </Space>
            <Table rowKey="id" size="small" loading={loading} dataSource={rows} columns={columns} scroll={{ x: 900 }}
                pagination={{ ...page, total, showSizeChanger: true, onChange: (current, pageSize) => setPage({ current, pageSize }) }} />
            <Modal open={adding} title="添加抑制" onOk={add} confirmLoading={saving} onCancel={() => setAdding(false)} destroyOnClose>
                <Form form={form} layout="vertical" initialValues={{ scope: 'global', reason: 'manual' }}>
                    <Form.Item name="emails" label="邮箱" rules={[{ required: true }]}><Input.TextArea rows={4} placeholder="每行一个" /></Form.Item>
                    <Space wrap>
                        <Form.Item name="scope" label="范围"><Select style={{ width: 120 }} options={SCOPES} /></Form.Item>
                        {scope && scope !== 'global' && (
                            <Form.Item name="scope_id" label={scope === 'user' ? '用户 ID' : '发信流 ID'} rules={[{ required: true }]}><InputNumber min={1} /></Form.Item>
                        )}
                        <Form.Item name="reason" label="原因"><Select style={{ width: 120 }} options={REASONS} /></Form.Item>
                    </Space>
                    <Form.Item name="category" label="类别（空 = 全部）" rules={[{ pattern: /^[A-Za-z0-9_.-]{1,64}$/, message: '类别代码无效' }]}><Input /></Form.Item>
                    <Form.Item name="note" label="备注" style={{ marginBottom: 0 }}><Input maxLength={255} /></Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default Suppressions;
